/**
 * Como uma peça da raia eletrônica se apresenta na tela (E78.F1.T06).
 *
 * O caso real GT-2026-0005 tem cinco CCBs e nenhuma traz número: as cinco saíam
 * como "CCB sem número informado", distinguíveis só pelo código de verificação
 * de 64 caracteres. A letra dá ao perito um nome curto para citar a peça, e o
 * nome do arquivo é o que ele reconhece do que enviou.
 */
import { ptBR } from '@/i18n/pt-BR';
import type { ExameEletronicoCcbData } from '@/lib/exame_eletronico';

const ALFABETO = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

/**
 * Letra da peça pela posição na lista: A, B, … Z, AA, AB…
 *
 * A continuação em duas letras existe porque nada no domínio limita um caso a
 * 26 peças — e uma peça sem letra voltaria a ser indistinguível.
 */
export function letraDaPeca(indice: number): string {
  if (indice < 0) return '';
  let n = indice;
  let letra = '';
  do {
    letra = `${ALFABETO[n % ALFABETO.length] ?? ''}${letra}`;
    n = Math.floor(n / ALFABETO.length) - 1;
  } while (n >= 0);
  return letra;
}

/** Título da peça: o número da CCB quando existe; letra + arquivo quando não. */
export function tituloDaPeca(ccb: ExameEletronicoCcbData, indice: number): string {
  if (ccb.ccb_number !== null && ccb.ccb_number !== '') {
    return ptBR.exameEletronico.ccbTitulo.replace('{numero}', ccb.ccb_number);
  }
  return ptBR.exameEletronico.pecaSemNumero
    .replace('{letra}', letraDaPeca(indice))
    .replace('{arquivo}', ccb.original_filename);
}

/** Mesma régua do servidor: 64 hex, espaços, dois-pontos e maiúsculas normalizados. */
const SHA256_HEX = /^[0-9a-f]{64}$/;

export function normalizarCodigo(valor: string): string {
  return valor.replace(/[\s:]/g, '').toLowerCase();
}

/** Vazio é válido (a peça pode não declarar código); preenchido tem de ser 64 hex. */
export function codigoValido(valor: string): boolean {
  const normalizado = normalizarCodigo(valor);
  return normalizado.length === 0 || SHA256_HEX.test(normalizado);
}

/**
 * O confronto entre o código declarado e o apurado, LIDO do quadro.
 *
 * Nunca recalculado na tela: quem confronta é o motor, e o item `hash_ccb` é o
 * veredito dele. Sem exame verificado a coluna fica pendente — e não "confere".
 */
export function confrontoDaPeca(
  ccb: ExameEletronicoCcbData,
): 'confere' | 'nao_confere' | 'ausente' | 'nao_executado' | null {
  const item = ccb.exame?.resultado?.integridade.find((i) => i.chave === 'hash_ccb');
  return item?.situacao ?? null;
}

/** Quantos indicadores de alerta o exame desta peça apurou. */
export function alertasDaPeca(ccb: ExameEletronicoCcbData): number | null {
  return ccb.exame?.resultado?.red_flags.length ?? null;
}
